/**
 * Sends clicks for rendered Elevate results. On a product page, it requests
 * the Product Page response and uses the selected Elevate variant's ticket for
 * successful Shopify add-to-cart events.
 */
(function initializeVoyadoNotifications(window, document) {
    'use strict';

    const context = window.VOYADO_CONTEXT;

    if (
        !context?.enabled ||
        !window.VOYADO_ELEVATE?.createClient
    ) {
        return;
    }

    const PDP_PRODUCT_KEY_PARAMETER = 'voyadoProductKey';
    const interactionNotificationsEnabled =
        context.notifications?.enabled === true;
    const client = window.VOYADO_ELEVATE.createClient();
    const debug = window.VOYADO_ELEVATE.debug;
    let activeResultTickets = null;

    function reportFailure(type, error) {
        console.warn(`[Voyado notifications] ${type} notification failed`, error);
    }

    function normalizeVariantKey(value) {
        return window.VOYADO_ELEVATE.normalizeVariantKey(
            value,
            context.productPage?.variantKeySource
        );
    }

    function endSession(requestContext, reason) {
        if (!requestContext?.customerKey || !requestContext?.sessionKey) {
            debug('End notification skipped', {
                reason: `no active Elevate session existed ${reason}`,
            });
            return;
        }

        debug(`Sending end notification ${reason}`);
        client
            .end(requestContext)
            .catch((error) => reportFailure('End', error));
    }

    function trackClick(card) {
        if (!interactionNotificationsEnabled) return;

        const ticket = String(card?.dataset.voyadoTicket || '').trim();

        if (!ticket) return;

        debug('Sending click notification', {
            sku: String(card.dataset.productSku || '').trim(),
            ticket,
        });
        client.click(ticket).catch((error) => reportFailure('Click', error));
    }

    function getVariantTickets(card) {
        try {
            const tickets = JSON.parse(
                card?.dataset.voyadoVariantTickets || 'null'
            );

            if (!tickets || typeof tickets !== 'object') return null;

            const entries = Object.entries(tickets)
                .filter(([variantKey, ticket]) => variantKey && ticket)
                .map(([variantKey, ticket]) => [
                    String(variantKey).trim(),
                    String(ticket).trim(),
                ]);

            return entries.length ? new Map(entries) : null;
        } catch {
            return null;
        }
    }

    async function loadProductPageTickets() {
        if (!document.documentElement.classList.contains('template-product')) {
            return null;
        }

        const urlProductKey = new URL(window.location.href).searchParams
            .get(PDP_PRODUCT_KEY_PARAMETER)?.trim();
        const productKey = urlProductKey;

        if (!productKey) {
            debug('Product-page ticket query skipped', {
                reason: 'no exact Elevate product key was supplied for this PDP',
            });
            return null;
        }

        try {
            // With productGroup.include left at its documented default (false),
            // ProductPageResult.tickets contains variantKey -> ticket entries
            // intended for add-to-cart notifications from this PDP.
            const response = await client.productPage({ productKey });
            const tickets = new Map(
                Object.entries(response?.tickets || {})
                    .filter(([variantKey, ticket]) => variantKey && ticket)
                    .map(([variantKey, ticket]) => [
                        String(variantKey).trim(),
                        String(ticket).trim(),
                    ])
            );

            debug('Loaded product-page add-to-cart tickets', {
                productKey,
                variantKeys: [...tickets.keys()],
            });

            return tickets;
        } catch (error) {
            reportFailure('Product-page query', error);
            return null;
        }
    }

    function ticketsFromProductPageResponse(response) {
        const tickets = new Map();

        for (const [variantKey, ticket] of Object.entries(response?.tickets || {})) {
            if (variantKey && ticket) {
                tickets.set(String(variantKey).trim(), String(ticket).trim());
            }
        }

        for (const product of response?.productGroup?.products || []) {
            for (const variant of product?.variants || []) {
                if (variant?.key && variant?.ticket) {
                    tickets.set(
                        String(variant.key).trim(),
                        String(variant.ticket).trim()
                    );
                }
            }
        }

        return tickets.size ? tickets : null;
    }

    function productPageTicketsPromise() {
        if (!context.productPage?.enabled) return loadProductPageTickets();

        return new Promise((resolve) => {
            function finish(state) {
                if (state?.status === 'success') {
                    resolve(ticketsFromProductPageResponse(state.response));
                    return true;
                }
                if (state?.status === 'error') {
                    resolve(null);
                    return true;
                }
                return false;
            }

            if (finish(window.VOYADO_PRODUCT_PAGE?.getState?.())) return;

            const handleState = (event) => {
                if (!finish(event.detail)) return;
                window.removeEventListener(
                    'voyado:product-page-state',
                    handleState
                );
            };

            window.addEventListener('voyado:product-page-state', handleState);
        });
    }

    const productPageTickets = interactionNotificationsEnabled
        ? productPageTicketsPromise()
        : Promise.resolve(null);

    document.addEventListener('click', (event) => {
        if (!interactionNotificationsEnabled) return;

        const trackedItem = event.target.closest('[data-voyado-ticket]');

        if (trackedItem) {
            activeResultTickets = getVariantTickets(trackedItem);
            trackClick(trackedItem);
            return;
        }

        const productCard = event.target.closest('.card--product');

        if (productCard) {
            debug('Click notification skipped', {
                sku: String(productCard.dataset.productSku || '').trim(),
                reason: 'product card has no ticket from an Elevate query',
            });
        }
    });

    document.addEventListener('ajaxProduct:added', async (event) => {
        if (!interactionNotificationsEnabled) return;

        const variantId = String(event.detail?.variantId || '');
        const sku = normalizeVariantKey(event.detail?.sku);
        const pageTickets = await productPageTickets;
        const resultTickets = activeResultTickets;
        const tickets = resultTickets || pageTickets;
        const ticketSource = resultTickets ? 'rendered-result' : 'product-page';

        // A rendered-result ticket selection belongs to one cart action only.
        // PDP tickets remain reusable for subsequent adds from the product page.
        activeResultTickets = null;

        if (!tickets || (!variantId && !sku)) {
            debug('Add-to-cart notification skipped', {
                variantId,
                sku,
                reason: !tickets
                    ? 'no Elevate variant tickets are associated with this add'
                    : 'Shopify variant ID and SKU are both missing',
            });
            return;
        }

        // Elevate variant keys follow the catalog feed. Shopify integrations
        // commonly use the variant ID, while some feeds use the SKU.
        const matchingVariantKey = [variantId, sku].find((key) => tickets.has(key));
        const ticket = matchingVariantKey
            ? tickets.get(matchingVariantKey)
            : null;

        if (!ticket) {
            debug('Add-to-cart notification skipped', {
                variantId,
                sku,
                elevateVariantKeys: [...tickets.keys()],
                ticketSource,
                reason: 'Shopify variant ID and SKU do not match an Elevate variant key',
            });
            return;
        }

        debug('Sending add-to-cart notification', {
            variantId,
            sku,
            matchingVariantKey,
            ticket,
            ticketSource,
        });
        client
            .addToCart(ticket)
            .catch((error) => reportFailure('Add-to-cart', error));
    });

    window.addEventListener('voyado:consent-updated', (event) => {
        if (event.detail?.hasConsent !== false) return;

        endSession(
            event.detail.previousRequestContext,
            'when consent was denied'
        );
    });

    const switchedAccountSession = window.VOYADO_SESSION
        ?.consumePendingSessionEndContext?.();

    if (switchedAccountSession) {
        endSession(
            switchedAccountSession,
            'because the Shopify account changed'
        );
    }

    window.VOYADO_NOTIFICATIONS = Object.freeze({ trackClick });
})(window, document);
